import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import GallerySection from '../components/GallerySection';
import InitialConsultationCallout from '../components/InitialConsultationCallout';
import { fetchOmOs, FALLBACK_OM_OS } from '../lib/cms';

export default function AboutPage() {
  const [educationExpanded, setEducationExpanded] = useState(false);
  const [pageData, setPageData] = useState(FALLBACK_OM_OS);

  useEffect(() => {
    async function load() {
      const data = await fetchOmOs();
      if (data) setPageData(data);
    }
    load();
  }, []);

  const portraitUrl = pageData.portraet_billede?.[0] || FALLBACK_OM_OS.portraet_billede[0];
  const portraitOptimized = portraitUrl.startsWith('http') && !portraitUrl.includes('/cdn-cgi/image/')
    ? portraitUrl.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/')
    : portraitUrl;

  const partnerUrl = pageData.samarbejdspartner_billede?.[0] || FALLBACK_OM_OS.samarbejdspartner_billede[0];
  const partnerOptimized = partnerUrl.startsWith('http') && !partnerUrl.includes('/cdn-cgi/image/')
    ? partnerUrl.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=240,quality=85,format=auto/')
    : partnerUrl;

  return (
    <div className="bg-white min-h-screen">
      <SEOHead
        title="Om KerneViden :: KerneViden"
        description="Mit navn er Joy, jeg er mor til 2 børn, haveentusiast, og et nysgerrigt menneske. Jeg holder af øjeblikke som en kop the i solen men en god bog, jord under neglende efter en dag i haven og de samtaler hvor man glemmer alt andet. Jeg er et menneske som elsker at grine og dele glæde sammen med andre...."
        ogTitle="Om KerneViden :: KerneViden"
        ogDescription="Mit navn er Joy, jeg er mor til 2 børn, haveentusiast, og et nysgerrigt menneske. Jeg holder af øjeblikke som en kop the i solen men en god bog, jord under neglende efter en dag i haven og de samtaler hvor man glemmer alt andet. Jeg er et menneske som elsker at grine og dele glæde sammen med andre...."
        ogImage="https://assets.auxodev.dk/assets/1d30bca3-a748-4ede-8335-828014d68a29/227b66a4-43a4-4089-81dc-ccbd5957ffe2_33952389.png"
      />

      {/* Hero Header */}
      <section className="pt-12 pb-16 bg-white border-b border-[#EAE2D7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#233424] font-normal tracking-tight">
            {pageData.hero_overskrift || "Mig & Kerneviden."}
          </h1>
          <p className="font-serif text-lg sm:text-xl text-[#526051] italic max-w-2xl mx-auto">
            {pageData.hero_undertitel || "Et trygt og fleksibelt rum, hvor mennesket kommer før systemet. Med afsæt i faglig viden."}
          </p>
        </div>
      </section>

      {/* Main Bio Section */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Joy's Portrait */}
            <div className="lg:col-span-5 space-y-6">
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  src={portraitOptimized}
                  alt="Joy - KerneViden"
                  className="w-full h-auto object-cover"
                />
              </div>

              {/* Uddannelsesbaggrund (expandable, no card box) */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => setEducationExpanded(!educationExpanded)}
                  className="w-full flex items-center justify-between text-left group cursor-pointer py-3 border-b border-[#E7DFD4] transition-colors"
                  aria-expanded={educationExpanded}
                >
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-graduation-cap text-base text-black" aria-hidden="true" />
                    <h3 className="font-serif text-lg sm:text-xl font-medium text-[#233424]">
                      {pageData.uddannelse_titel || "Min uddannelses baggrund."}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-[#7A8A78] group-hover:text-[#233424] transition-colors">
                    <span>{educationExpanded ? 'Skjul' : 'Vis'}</span>
                    <i
                      className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                        educationExpanded ? 'rotate-180' : ''
                      }`}
                      aria-hidden="true"
                    />
                  </div>
                </button>

                {educationExpanded && (
                  <div className="pt-4 pl-1 text-sm text-[#4E5C4E]">
                    {pageData.uddannelse_punkter?.includes('<') ? (
                      <div
                        dangerouslySetInnerHTML={{ __html: pageData.uddannelse_punkter }}
                        className="prose prose-sm max-w-none text-[#4E5C4E] [&>ul]:space-y-3 [&>ul]:list-none [&>ul]:pl-0 [&>ul>li]:flex [&>ul>li]:items-start [&>ul>li]:gap-2.5 [&>ul>li]:before:content-['✓'] [&>ul>li]:before:text-black [&>ul>li]:before:font-bold [&>ul>li]:before:text-xs [&>ul>li]:before:mt-0.5"
                      />
                    ) : (
                      <ul className="space-y-3 text-sm text-[#4E5C4E]">
                        <li className="flex items-start gap-2.5">
                          <i className="fa-solid fa-check text-xs text-black mt-1 shrink-0" aria-hidden="true" />
                          <span>Socialpædagog. (Bachelor i Social og Special pædagogik).</span>
                        </li>
                        <li className="flex items-start gap-2.5">
                          <i className="fa-solid fa-check text-xs text-black mt-1 shrink-0" aria-hidden="true" />
                          <span>Specialiceret uddannelse i autismespektrum forstyrelse. (Molis psykologisk udviklingscenter)</span>
                        </li>
                        <li className="flex items-start gap-2.5">
                          <i className="fa-solid fa-check text-xs text-black mt-1 shrink-0" aria-hidden="true" />
                          <span>Igangværende Neuroaffektiv Psykoterapeut (4år)</span>
                        </li>
                        <li className="flex items-start gap-2.5">
                          <i className="fa-solid fa-check text-xs text-black mt-1 shrink-0" aria-hidden="true" />
                          <span>Og en hulens masse bøger af ren interesse.</span>
                        </li>
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Verbatim Biography text */}
            <div className="lg:col-span-7 space-y-8">
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl text-[#233424] font-medium tracking-tight mb-4">
                  {pageData.biografi_overskrift || "Kære dig."}
                </h2>
              </div>

              <div className="text-base text-[#455244] leading-relaxed space-y-6">
                <p>
                  Mit navn er Joy, jeg er mor til 2 børn, haveentusiast, og et nysgerrigt menneske. Jeg holder af øjeblikke som en kop the i solen men en god bog, jord under neglende efter en dag i haven og de samtaler hvor man glemmer alt andet. Jeg er et menneske som elsker at grine og dele glæde sammen med andre. I rummet sammen med mig, vil du højst sandsynlig opleve at jeg har smidt skoene og trukket benene op i stolen. Jeg ønsker at skabe et rum hvor du oplever at blive taget alvorligt, hvor der også er plads til grin, tårer og alt det midt i mellem.
                </p>

                <div className="space-y-2 pt-2">
                  <h3 className="font-serif text-lg font-medium text-[#233424]">
                    Min arbejdserfaring stammer primært fra kommunalt og privat regi som:
                  </h3>
                  <ul className="space-y-2 text-sm sm:text-base text-[#4E5C4E] list-disc pl-5">
                    <li>Vejleder. /Støtte kontaktperson./Familiebehandler.</li>
                    <li>Udvikling af tilbud for og sammen med unge.</li>
                    <li>Undervisning i neurodivagens til konsulenter.</li>
                    <li>Faglig ansvarlig for konsulenter (familiebehandlere, kontaktperson mm.)</li>
                    <li>Selvforståelsesforløb børn, unge og forældre til og med neurodivagens.</li>
                  </ul>
                </div>

                <p>
                  <strong>KerneViden</strong> er skabt ud fra et ønske om at tilbyde et trygt og fleksibelt rum, hvor mennesket kommer før systemet. Med afsæt i faglig viden.
                </p>

                <p>
                  I KerneViden arbejder jeg ud fra en grundlæggende forståelse af, at mennesker rummer ressourcer og muligheder for udvikling, når de bliver mødt med den rette støtte, viden og forståelse.
                </p>

                <div className="border-l-4 border-black pl-5 space-y-4 py-1">
                  <blockquote className="text-[#233424] font-serif italic text-base sm:text-lg font-medium">
                    ''Menneskers adfærd fortæller os en historie om en krop der har passet på os.''
                  </blockquote>
                  <p>
                    Det, vi oplever som udfordringer, mistrivsel, fastlåste mønstre, eller angst er ofte blevet skabt i samspillet mellem vores erfaringer, vores relationer og vores nervesystem.
                  </p>
                </div>

                <p>
                  Nogle gange fortsætter kroppen og sindet med at reagere ud fra tidligere erfaringer, selvom vores liv har ændret sig.
                </p>

                <p>
                  Det kan komme til udtryk som:
                </p>

                <p className="border-l-4 border-[#E7DFD4] pl-4 py-0.5 italic text-[#455244]">
                  Overtænkning, overbelastning, følelsesmæssige reaktioner, vanskeligheder i relationer eller en oplevelse af ikke helt at forstå sig selv.
                </p>

                <p>
                  Jeg ser reaktioner og adfærd som vigtige signaler, der kan hjælpe os med at forstå de behov, erfaringer og mønstre, der ligger bag. For mig er det afgørende at arbejde med kernen hos mennesker. fordi de symptomer vi bære på ofte er signaler fra vores inderste.
                </p>

                <p>
                  Mit arbejde bygger på et helhedsorienteret perspektiv, hvor jeg inddrager viden fra neuroaffektiv udviklingspsykologi, relationel forståelse, traumeforståelse, psykologiske teorier og neurodiversitet.
                </p>

                <p>
                  Jeg tilbyder støtte og vejledning til unge, voksne og familier, som ønsker større forståelse af sig selv og hinanden.
                </p>

                <blockquote className="p-5 rounded-2xl bg-white shadow-xs text-[#233424] font-serif italic text-base sm:text-lg border border-[#E7DFD4] border-l-4 border-l-[#233424]">
                  ''KerneViden handler om at skabe forbindelse mellem det, vi oplever på overfladen, og den kerne af ressourcer, forståelse og muligheder, der findes under.''
                </blockquote>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Samarbejdspartnere: Lev i balance */}
      <section className="py-16 bg-white border-y border-[#EFEAE2]" aria-label="Samarbejdspartnere">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#233424] font-normal tracking-tight">
              KerneVidens samarbejdspartnere.
            </h2>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 lg:gap-8">
            <img
              src={partnerOptimized}
              alt={pageData.samarbejdspartner_titel || "Lev i balance."}
              className="h-24 w-24 sm:h-28 sm:w-28 rounded-2xl object-cover shrink-0 border border-[#E3D9CC]"
            />
            <div className="space-y-2 sm:space-y-3">
              <h3 className="font-serif text-2xl font-medium text-[#233424]">
                {pageData.samarbejdspartner_titel || "Lev i balance."}
              </h3>
              <p className="text-sm sm:text-base leading-relaxed text-[#4E5C4E] max-w-3xl">
                {pageData.samarbejdspartner_beskrivelse || "Jeg er glad for at være samarbejdspartner med Lev i Balance, som er et behandlingssted for spiseforstyrrelser i Roskilde. Jeg oplever at vi deler mange af de samme værdier omkring nærvær, faglighed og et helhedsorienteret blik på mennesker"}
              </p>
              {pageData.samarbejdspartner_link_url && (
                <a
                  href={pageData.samarbejdspartner_link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm font-semibold text-[#233424] hover:text-black transition-colors pt-1"
                >
                  <span>{pageData.samarbejdspartner_link_tekst || "Besøg levibalance.nu"}</span>
                  <i className="fa-solid fa-arrow-up-right-from-square text-xs ml-1.5" aria-hidden="true" />
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Consultation callout */}
      <InitialConsultationCallout
        title={pageData.samtale_overskrift || "Tag første skridt med en gratis samtale."}
        description={pageData.samtale_beskrivelse || "Samtalen er helt uforpligtende og kan foregå telefonisk eller online, så du kan mærke efter om kemien passer til dig."}
        ctaText={pageData.samtale_knap_tekst || "Book gratis samtale"}
      />
    </div>
  );
}
